import React, { startTransition, useCallback, useEffect, useId, useState } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { noop } from 'lodash-es';
import { connectToModel } from 'client/data/luckdragon/redux/react-binding';
import { ReviewsModel, ReviewsPaths } from 'client/data/models/dealer-reviews';
import { DEALER_PROPS, REVIEWS_LIST_TYPES, REVIEWS_PER_CLICK } from 'client/constants/dealer-reviews';
import { Button } from 'reactstrap';
import { ScrollLink } from 'site-modules/shared/components/scroll-link/scroll-link';
import { ReviewListHeader } from 'site-modules/dealerships/components/dealer-reviews-section/components/review-list-header/review-list-header';
import { WithLoading } from 'site-modules/dealerships/components/dealer-reviews-section/components/with-loading/with-loading';
import { ReviewListItems } from 'site-modules/dealerships/components/dealer-reviews-section/components/review-list-items/review-list-items';

function BaseReviewsListUI({
  listType,
  reviews,
  params,
  reviewsType,
  initialCount,
  totalReviews,
  setModelValue,
  children = undefined,
  newOldReviewsCount,
  onMaxAmountExpanded,
  onCloseReviewsList = noop,
  isMobile,
}) {
  const [isLoading, setIsLoading] = useState(false);
  const [numVisible, setNumVisible] = useState(initialCount);
  const id = useId();

  useEffect(() => {
    startTransition(() => setNumVisible(initialCount));
  }, [initialCount]);

  useEffect(() => {
    if (numVisible >= reviews.length && reviews.length < totalReviews) {
      onMaxAmountExpanded(true);
    }
  }, [numVisible, onMaxAmountExpanded, reviews.length, totalReviews]);

  useEffect(() => {
    if (reviews) {
      startTransition(() => setIsLoading(false));
    }
  }, [reviews]);

  const showMore = useCallback(() => {
    setNumVisible(prev => prev + REVIEWS_PER_CLICK);
  }, []);

  const seeLess = useCallback(() => {
    setNumVisible(initialCount);
    onCloseReviewsList();
  }, [initialCount, onCloseReviewsList]);

  const handleSortReviews = useCallback(
    ({ sortBy, order }) => {
      setModelValue(ReviewsPaths.getReviewsFilterPath(), ReviewsModel, { sortBy, order, page: '1' });
      setIsLoading(true);
    },
    [setModelValue]
  );

  const isReviewsCollapsed = numVisible === 0;
  const showReadMoreButton = numVisible < reviews.length;
  const scrollId = `${id}-${listType}`;

  return (
    <div className="mt-2" id={scrollId}>
      <div className={classnames({ 'd-none': isReviewsCollapsed })}>
        {listType === REVIEWS_LIST_TYPES.OLD && (
          <div className="size-14 text-cool-gray-40 bg-cool-gray-90 text-center rounded-8 p-0_5 mb-1_5">
            The following reviews are more than two years old. They are not included in the overall rating.
          </div>
        )}

        <ReviewListHeader
          totalReviews={totalReviews}
          reviewsType={reviewsType}
          onSortChange={handleSortReviews}
          withSorting={newOldReviewsCount >= 2}
        />

        <WithLoading isLoading={isLoading}>
          <ReviewListItems reviews={reviews} numVisible={numVisible} params={params} />
          {!!(numVisible - initialCount) && (
            <div className="text-end mt-1">
              <ScrollLink
                to={scrollId}
                scrollConfig={{ offset: -160 }}
                className="btn btn-link px-0 py-0 text-transform-none fw-normal size-14"
                onClick={seeLess}
              >
                See less
              </ScrollLink>
              <hr className="my-0" />
            </div>
          )}
        </WithLoading>
      </div>

      {showReadMoreButton && (
        <div className="text-center mt-2">
          <Button
            color="blue-50"
            className={classnames('rounded-8 text-transform-none fw-medium size-16', { 'w-100': isMobile })}
            onClick={showMore}
            data-tracking-id="ddp_view_dealer_reviews"
            data-tracking-value={`read ${reviewsType} reviews`}
          >
            Show More Reviews
          </Button>
        </div>
      )}

      {children && <div className={classnames({ 'd-none': showReadMoreButton || isLoading })}>{children}</div>}
    </div>
  );
}

BaseReviewsListUI.propTypes = {
  listType: PropTypes.string.isRequired,
  reviews: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  params: DEALER_PROPS.isRequired,
  reviewsType: PropTypes.string.isRequired,
  initialCount: PropTypes.number.isRequired,
  totalReviews: PropTypes.number.isRequired,
  children: PropTypes.node,
  setModelValue: PropTypes.func.isRequired,
  newOldReviewsCount: PropTypes.number.isRequired,
  onMaxAmountExpanded: PropTypes.func.isRequired,
  onCloseReviewsList: PropTypes.func,
  isMobile: PropTypes.bool.isRequired,
};

export const BaseReviewsList = connectToModel(BaseReviewsListUI, {});
